import ReactDOM, { Root } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { Theme } from '@radix-ui/themes'
import '@radix-ui/themes/styles.css'
import { Provider } from 'react-redux'
import { store, useAppDispatch } from './reducers/store.ts'
import { Navigate, Outlet, RouterProvider } from 'react-router-dom'
import LoginWrapper from './components/login/LoginWrapper'
import { LOGIN_ROUTE } from './strings/api-consts.ts'
import { fetchUserSession } from './reducers/users/users.thunk.ts'
import SettingsWrapper from './components/settings/SettingsWrapper.tsx'
import SessionHydrater from './components/login/SessionHydrater.tsx'
import AdminPassword from './components/admin-password/AdminPassword.tsx'
import { Toaster } from 'react-hot-toast'
import { SessionState } from './types/UserTypes.ts'
import ErrorWrapper from './components/error/ErrorWrapper.tsx'
import Forbidden from './components/error/Forbidden.tsx'
import MembersWrapper from './components/members/MembersWrapper.tsx'
import AdversariesWrapper from './components/adversaries/AdversariesWrapper.tsx'
import AlertsWrapper from './components/alert-console/AlertsWrapper.tsx'
import DevicesWrapper from './components/devices/DevicesWrapper.tsx'
import DomainBlocklistWrapper from './components/domain-blocklist/DomainBlocklistWrapper.tsx'
import IntegrationsWrapper from './components/integrations/IntegrationsWrapper.tsx'
import DashboardWrapper from './components/dashboard/DashboardWrapper.tsx'
import SsoErrorWrapper from './components/sso-error/SsoErrorWrapper.tsx'
import VisualizationsWrapper from './components/network-intel/VisualizationsWrapper.tsx'
import IntelReportsWrapper from './components/intel-reports/IntelReportsWrapper.tsx'
import VictimologyWrapper from './components/victimology/VictimologyWrapper.tsx'
import { LDProvider } from 'launchdarkly-react-client-sdk'
import DeviceOverview from './components/devices/DeviceOverview.tsx'
import ThreatHunterWrapper from './components/threat-hunter/ThreatHunterWrapper.tsx'
import VisualizationsHeader from './components/header/VisualizationsHeader.tsx'
import DashboardHeader from './components/header/DashboardHeader.tsx'
import Header from './components/header/Header.tsx'
import DevicesHeader from './components/header/DevicesHeader.tsx'
import ThreatHunterHeader from './components/header/ThreatHunterHeader.tsx'
import TravelWrapper from './components/travel/TravelWrapper.tsx'
import NotFound from './components/error/NotFound.tsx'
import IntegrationsHeader from './components/header/IntegrationsHeader.tsx'
import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6'
import { datadogLogs } from '@datadog/browser-logs'
import { reportErrorToDatadog } from './utils/error-logging.ts'
import PoliciesHeader from './components/header/PoliciesHeader.tsx'
import NewDevicePoliciesWrapper from './components/device-policies/NewDevicePoliciesWrapper.tsx'
import DevicePolicyOverviewWrapper from './components/device-policies/DevicePolicyOverviewWrapper.tsx'
import MtdCompleteWrapper from './components/mtd-complete/MtdCompleteWrapper.tsx'
import CreatePolicyWrapper from './components/device-policies/CreatePolicyWrapper.tsx'
import GroupsWrapper from './components/groups/GroupsWrapper.tsx'
import GroupsHeader from './components/header/GroupsHeader.tsx'
import Group from './components/groups/Group.tsx'

if (import.meta.env.VITE_PRETTY_ENV !== 'Local' && import.meta.env.VITE_ON_PREM === 'false') {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DD_APP_ID,
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'iverify-edr-portal',
    env: import.meta.env.VITE_PRETTY_ENV,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    defaultPrivacyLevel: 'mask',
    plugins: [reactPlugin({ router: true })],
  })

  datadogRum.startSessionReplayRecording()

  datadogLogs.init({
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'iverify-edr-portal',
    env: import.meta.env.VITE_PRETTY_ENV,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  })

  window.addEventListener('error', (event) => {
    if (event.error) {
      reportErrorToDatadog(event.error, { source: 'window.onerror' })
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error) {
      reportErrorToDatadog(event.reason, { source: 'unhandledrejection' })
    } else {
      datadogLogs.logger.error('Unhandled promise rejection', {
        reason: event.reason,
      })
    }
  })
}

if (import.meta.env.VITE_PRETTY_ENV !== 'Production') {
  document.title = '[' + import.meta.env.VITE_PRETTY_ENV + '] ' + 'iVerify EDR Portal'
} else {
  document.title = 'iVerify EDR Portal'
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <PrivateRoute></PrivateRoute>,
    children: [
      {
        path: '/',
        element: (
          <App pageName="DASHBOARD" header={<DashboardHeader />}>
            <DashboardWrapper />
          </App>
        ),
      },
      {
        path: '/dashboard',
        element: (
          <App pageName="DASHBOARD" header={<DashboardHeader />}>
            <DashboardWrapper />
          </App>
        ),
      },
      {
        path: '/settings',
        element: <SettingsWrapper />,
      },
      {
        path: '/members',
        element: (
          <App pageName="MEMBERS" header={<Header pageTitle={'Members'} />}>
            <MembersWrapper />
          </App>
        ),
      },
      {
        path: '/groups',
        element: (
          <App pageName="GROUPS" header={<Header pageTitle={'Groups'} />}>
            <GroupsWrapper />
          </App>
        ),
      },
      {
        path: '/groups/:groupId',
        element: (
          <App pageName="GROUPS" header={<GroupsHeader pageTitle={'Groups'} />}>
            <Group />
          </App>
        ),
      },
      {
        path: '/travel',
        element: (
          <App pageName="TRAVEL" pageTitle="Location Insights">
            <TravelWrapper />
          </App>
        ),
      },
      {
        path: '/integrations',
        element: (
          <App pageName="INTEGRATIONS" header={<IntegrationsHeader pageTitle={'Integrations'} />}>
            <IntegrationsWrapper />
          </App>
        ),
      },
      {
        path: '/adversaries',
        element: (
          <App pageName="ADVERSARIES" header={<VisualizationsHeader currentPage="ADVERSARIES" />}>
            <AdversariesWrapper />
          </App>
        ),
      },
      {
        path: '/blocklist',
        element: (
          <App pageName="BLOCKLIST" header={<Header pageTitle={'Blocklist'} />}>
            <DomainBlocklistWrapper />
          </App>
        ),
      },
      {
        path: '/device-policies',
        element: (
          <App pageName="DEVICE-POLICIES" header={<PoliciesHeader pageTitle={'Policies'} />}>
            <NewDevicePoliciesWrapper />
          </App>
        ),
      },
      {
        path: '/device-policies/:policyId',
        element: (
          <App pageName="DEVICE-POLICIES" header={<PoliciesHeader pageTitle={'Policies'} overviewScreen={true} />}>
            <DevicePolicyOverviewWrapper />
          </App>
        ),
      },
      {
        path: '/devices',
        element: (
          <App pageName="DEVICES" header={<DevicesHeader pageTitle={'Devices'} />}>
            <DevicesWrapper />
          </App>
        ),
      },
      {
        path: '/devices/:deviceId',
        element: (
          <App pageName="DEVICES" header={<DevicesHeader pageTitle={'Devices'} />}>
            <DeviceOverview />
          </App>
        ),
      },
      {
        path: '/alerts',
        element: (
          <App pageName="ALERTS" header={<Header pageTitle={'Alerts'} />}>
            {' '}
            <AlertsWrapper />
          </App>
        ),
      },
      {
        path: '/sso-error',
        element: (
          <App pageName="SSO_ERROR">
            <SsoErrorWrapper />
          </App>
        ),
      },
      {
        path: '/intel-reports',
        element: (
          <App pageName="INTEL-REPORTS" header={<VisualizationsHeader currentPage="INTEL-REPORTS" />}>
            <IntelReportsWrapper />
          </App>
        ),
      },
      {
        path: '/visualizations',
        element: (
          <App pageName="VISUALIZATIONS" header={<VisualizationsHeader currentPage="VISUALIZATIONS" />}>
            <VisualizationsWrapper />
          </App>
        ),
      },
      {
        path: '/victimology',
        element: (
          <App pageName="VICTIMOLOGY" header={<VisualizationsHeader currentPage="VICTIMOLOGY" />}>
            <VictimologyWrapper />
          </App>
        ),
      },
      {
        path: '/threat-hunter',
        element: (
          <App pageName="THREAT-HUNTER" header={<ThreatHunterHeader pageTitle={'Threat Hunter'} />}>
            <ThreatHunterWrapper />
          </App>
        ),
      },
      {
        path: '/create-device-policy',
        element: (
          <App
            pageName="CREATE-DEVICE-POLICY"
            header={<PoliciesHeader createScreen={true} pageTitle={'Create Policy'} />}
          >
            <CreatePolicyWrapper />
          </App>
        ),
      },
      {
        path: '/mtd/consent-complete',
        element: <MtdCompleteWrapper />,
      },
    ],
  },
  {
    path: '/login',
    element: <LoginWrapper />,
  },
  {
    path: '/forbidden',
    element: <Forbidden />,
  },
  {
    path: '/sso-login',
    element: <SessionHydrater />,
  },
  {
    path: '/update-password/:token',
    element: <AdminPassword />,
  },
  {
    path: '/error',
    element: <ErrorWrapper />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
])

// Check if root is already created to avoid duplicate createRoot calls
const rootElement = document.getElementById('root') as HTMLElement
const existingRoot = (rootElement as { _reactRootContainer?: Root })._reactRootContainer
if (!existingRoot) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <Provider store={store}>
      <LDProvider clientSideID={import.meta.env.VITE_LD_CLIENT_ID}>
        <Theme scaling="100%" accentColor="violet">
          <RouterProvider router={router} />
          <Toaster
            toastOptions={{
              style: {
                maxWidth: 500,
              },
            }}
          />
        </Theme>
      </LDProvider>
    </Provider>
  )
} else {
  existingRoot.render(
    <Provider store={store}>
      <LDProvider clientSideID={import.meta.env.VITE_LD_CLIENT_ID}>
        <Theme scaling="100%">
          <RouterProvider router={router} />
          <Toaster
            toastOptions={{
              style: {
                maxWidth: 500,
              },
            }}
          />
        </Theme>
      </LDProvider>
    </Provider>
  )
}
// eslint-disable-next-line react-refresh/only-export-components
function PrivateRoute() {
  const di = useAppDispatch()
  di(fetchUserSession())
  const session = JSON.parse(localStorage.getItem('session') as string) as SessionState
  const isAuthenticated = session && session?.user?.email
  return isAuthenticated ? <Outlet /> : <Navigate to={LOGIN_ROUTE} />
}
